import { RunStatusBadge } from '@/components/MonitoringView/RunsSidebar/RunStatusBadge';
import { MonitoringListItem } from '@/components/MonitoringView/shared/MonitoringListItem';
import { ResubmissionBadge } from '@/components/MonitoringView/shared/resubmit/ResubmissionBadge';
import { formatDateTime, useRelativeTime, formatDuration } from '@/utils/dateTime';
import { Flash16Filled } from '@fluentui/react-icons';
import { WorkflowRunsPanelStyles } from './WorkflowRunsPanel.styles';
import type { Run } from '@/types/monitoring';

type RunCardProps = {
  run: Run;
  isSelected: boolean;
  isFocused: boolean;
  /** Roving tab stop within the runs listbox. */
  isTabStop?: boolean;
  onSelect: (runId: string) => void;
  onRef: (id: string, el: HTMLDivElement | null) => void;
  /** Multi-select mode for bulk retry; `isSelected` then means "currently viewed". */
  selectionMode?: boolean;
  isChecked?: boolean;
};

export const RunCard = ({
  run,
  isSelected,
  isFocused,
  isTabStop,
  onSelect,
  onRef,
  selectionMode = false,
  isChecked = false,
}: RunCardProps) => {
  const styles = WorkflowRunsPanelStyles();

  const relativeTime = useRelativeTime(run.startTime);

  const meta = (
    <>
      {formatDateTime(run.startTime, 'adaptive')}
      {run.isDraftRun && (
        <span className={styles.draftIndicator}>
          {' · '}
          <Flash16Filled className={styles.draftIcon} />
          Draft
        </span>
      )}
      {run.isAgentic && ` · ${run.executionCount} turn${run.executionCount !== 1 ? 's' : ''}`}
    </>
  );

  return (
    <MonitoringListItem
      id={run.id}
      status={run.status}
      primaryText={relativeTime}
      badge={
        <>
          <RunStatusBadge status={run.status} size="small" />
          {run.previousRunId && <ResubmissionBadge />}
        </>
      }
      metaText={meta}
      trailingText={formatDuration(run.duration, { fallback: '--' })}
      isSelected={isSelected}
      isFocused={isFocused}
      {...(isTabStop !== undefined ? { isTabStop } : {})}
      onSelect={onSelect}
      onRef={onRef}
      tooltipContent={formatDateTime(run.startTime, 'weekday')}
      selectionMode={selectionMode}
      isChecked={isChecked}
      isCurrent={isSelected}
      testId={`run-card-${run.id}`}
    />
  );
};
